import { useEffect, useState } from 'react';

/**
 * Page state for a client-side-sliced table.
 *
 * Replaces the `const [page, setPage] = useState(1)` + ad-hoc reset effects that
 * were copy-pasted across the GA4 tabs. Two guarantees the hand-rolled version
 * lacked:
 *
 *  1. **Clamped** — the returned `page` is never greater than `totalPages`. The
 *     old code let a stale page survive a dataset change, producing a table with
 *     headers and zero rows plus a nonsensical "Showing 91–32 of 32" label
 *     (reproduced live: page 10 of 10, then narrow the date range to 32 rows).
 *     Clamping fixes the render on the SAME pass, so there is no frame where the
 *     user sees an empty table.
 *
 *  2. **Auto-reset** — `setPage(1)` fires when `resetKey` changes, so a new fetch
 *     or a changed filter starts at the top. Pass whatever identifies the
 *     dataset: the rows array, a search string, a filter id, or an array of them.
 *
 * Note the clamp alone is sufficient to prevent the empty-table bug; the reset is
 * what makes "narrow the filter" land on page 1 rather than on the last page.
 *
 * @param {number} totalItems  Row count AFTER filtering.
 * @param {number} perPage     Rows per page.
 * @param {any[]}  resetKey    Dependency list; changing any entry resets to page 1.
 * @returns {{ page: number, setPage: Function, totalPages: number, sliceStart: number, sliceEnd: number }}
 */
const useTablePage = (totalItems, perPage, resetKey = []) => {
  const [rawPage, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / perPage));
  const page = Math.min(Math.max(1, rawPage), totalPages);

  // `resetKey` is a caller-supplied dependency list, so it is spread as the
  // effect's deps. Callers must keep its length constant across renders (React
  // requires a fixed dep-array size) — every call site passes a literal array.
  useEffect(() => { setPage(1); }, resetKey);

  return {
    page,
    setPage,
    totalPages,
    sliceStart: (page - 1) * perPage,
    sliceEnd: page * perPage,
  };
};

export default useTablePage;
